import { ApolloClient } from "@apollo/client";
import { useState } from "react";
import FileUpload, { useFileUpload } from "./FileUpload";
import makeGetSignedPutUrl, {
  SignedPutUrlData,
  SignedPutUrlMetadata,
} from "./signedPutUrl";

export type SignedPutUrlFileUploadOptions = {
  apolloClient: ApolloClient<object>;
  file: File;
  filePath?: string;
  id?: string;
  metadata?: SignedPutUrlMetadata;
};

export default class SignedPutUrlFileUpload extends FileUpload {
  public apolloClient: ApolloClient<object>;

  public filePath?: string;

  public metadata?: SignedPutUrlMetadata;

  protected getSignedPutUrl: (
    file: File,
    filePath?: string,
    metadata?: SignedPutUrlMetadata,
  ) => Promise<SignedPutUrlData>;

  public signedUrlData: SignedPutUrlData | undefined;

  public isUploadingToPath = false;

  constructor({
    apolloClient,
    file,
    filePath,
    id,
    metadata,
  }: SignedPutUrlFileUploadOptions) {
    super(file, id);

    this.apolloClient = apolloClient;
    this.filePath = filePath;
    this.metadata = metadata;

    this.getSignedPutUrl = makeGetSignedPutUrl(apolloClient);
  }

  async startUploadToPath() {
    this.isUploadingToPath = true;

    this.dispatchEvent(new Event("startUploadToPath"));

    const allMetadata = { "file-id": this.id, ...this.metadata };

    try {
      this.signedUrlData = await this.getSignedPutUrl(
        this.file,
        this.filePath,
        allMetadata,
      );
    } catch (e) {
      if (e instanceof Error) {
        throw this.fileUploadError(e);
      } else {
        throw e;
      }
    }

    if (!this.signedUrlData?.url) {
      throw this.fileUploadError("Could not get signed URL from response.");
    }

    return this.startUpload(this.signedUrlData.url, {
      method: "PUT",
    });
  }

  retryUploadToPath(forceNewSignedUrl = false) {
    if (this.signedUrlData && this.lastUploadParams && !forceNewSignedUrl)
      return this.retryUpload();
    return this.startUploadToPath();
  }

  getSentryExtra() {
    const base = super.getSentryExtra();
    return {
      ...base,
      filePath: this.filePath,
      metadata: this.metadata,
      key: this.signedUrlData?.key,
    };
  }
}

export function useSignedPutUrlFileUpload(upload: SignedPutUrlFileUpload) {
  const [isLoadingSignedUrl, setIsLoadingSignedUrl] = useState(
    upload.isUploadingToPath,
  );

  upload.addEventListener("startUploadToPath", () =>
    setIsLoadingSignedUrl(true),
  );

  upload.addEventListener("done", () => {
    setIsLoadingSignedUrl(false);
  });

  upload.addEventListener("error", () => {
    setIsLoadingSignedUrl(false);
  });

  const { loading, progress, isDone, error } = useFileUpload(upload);

  return { loading: loading || isLoadingSignedUrl, isDone, progress, error };
}
